import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Seo } from '../components/Seo';
import { PageHeader } from '../components/common/PageHeader';
import { Chip } from '../components/common/Chip';
import { Icon } from '../components/common/Icon';
import { Lightbox } from '../components/gallery/Lightbox';
import { captureFlip, runFlip, type FlipState } from '../anim/galleryFlip';
import { useReducedMotion } from '../anim/useReducedMotion';
import { breadcrumbSchema } from '../lib/seo';
import { cn } from '../lib/cn';
import { useGallery } from '../hooks';

export default function GalleryPage() {
  const { data } = useGallery();
  const galleryItems = data?.items ?? [];
  const galleryCategories = data?.categories ?? [{ slug: 'all', name: 'All' }];
  const placeholder = data?.placeholder ?? false;

  const [category, setCategory] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const reduced = useReducedMotion();
  const gridRef = useRef<HTMLDivElement>(null);
  const flipRef = useRef<FlipState | null>(null);

  const filtered = useMemo(
    () => (category === 'all' ? galleryItems : galleryItems.filter((i) => i.category === category)),
    [category, galleryItems],
  );
  const visibleKey = filtered.map((i) => i.id).join(',');

  const changeCategory = (slug: string) => {
    if (!reduced && gridRef.current) {
      flipRef.current = captureFlip(Array.from(gridRef.current.querySelectorAll('[data-flip-id]')));
    }
    setLightboxIndex(null);
    setCategory(slug);
  };

  useLayoutEffect(() => {
    if (flipRef.current) {
      runFlip(flipRef.current);
      flipRef.current = null;
    }
  }, [visibleKey]);

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: galleryItems.length };
    for (const c of galleryCategories) {
      if (c.slug === 'all') continue;
      map[c.slug] = galleryItems.filter((i) => i.category === c.slug).length;
    }
    return map;
  }, [galleryItems, galleryCategories]);

  return (
    <>
      <Seo
        title="Gallery — Farmer Meetings, Field Demos & Events | GreenbayBio"
        description="GreenbayBio in the field: farmer meetings, field demonstrations, crop results, product launches, retailer meetings, training and company events."
        path="/gallery"
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Gallery', path: '/gallery' }])]}
      />
      <PageHeader
        eyebrow="On the ground"
        title="GreenbayBio in the field."
        subtitle="Farmer meetings, demonstrations, crop results and events — out where the work actually happens."
      />

      <div className="container-x py-12">
        {galleryItems.length === 0 ? (
          <div className="flex flex-col items-center rounded-xl border border-dashed border-sand-200 bg-white py-20 text-center">
            <Icon name="Sprout" className="h-10 w-10 text-ink-300" />
            <h2 className="mt-4 font-display text-xl font-semibold text-ink">Event photos are on the way</h2>
            <p className="mt-2 max-w-sm text-ink-500">Published events with photos will appear here.</p>
          </div>
        ) : (
          <>
            <div className="mb-8 flex flex-wrap gap-2.5" role="group" aria-label="Filter gallery by category">
              {galleryCategories.map((c) => (
                <Chip key={c.slug} active={category === c.slug} onClick={() => changeCategory(c.slug)} count={counts[c.slug]}>
                  {c.name}
                </Chip>
              ))}
            </div>

            <div ref={gridRef} className="grid auto-rows-[150px] grid-cols-2 gap-3 sm:auto-rows-[170px] md:grid-cols-3 lg:grid-cols-4">
              {filtered.map((item, i) => (
                <button
                  key={item.id}
                  data-flip-id={item.id}
                  onClick={() => setLightboxIndex(i)}
                  className={cn('group relative overflow-hidden rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-bright')}
                  style={{ gridRow: item.height > item.width ? 'span 2' : 'span 1' }}
                  aria-label={`Open ${item.title}`}
                >
                  <img
                    src={item.src}
                    alt={item.title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 ease-premium group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="absolute inset-x-0 bottom-0 translate-y-2 p-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                    <p className="text-sm font-semibold text-white">{item.title}</p>
                    {item.caption && <p className="text-xs text-white/70">{item.caption}</p>}
                  </div>
                </button>
              ))}
            </div>

            {placeholder && (
              <p className="mt-6 text-xs text-ink-500">
                Note: images shown are branded placeholders — real event photography will replace them
                during integration.
              </p>
            )}
          </>
        )}
      </div>

      <Lightbox items={filtered} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={setLightboxIndex} />
    </>
  );
}
